import React from "react";
import { useTranslation } from "react-i18next";
import { Box, Typography } from "@mui/material";
import HeadsetMicOutlinedIcon       from "@mui/icons-material/HeadsetMicOutlined";
import AccountTreeOutlinedIcon      from "@mui/icons-material/AccountTreeOutlined";
import AltRouteOutlinedIcon         from "@mui/icons-material/AltRoute";
import WorkOutlinedIcon             from "@mui/icons-material/WorkOutlined";
import AutoAwesomeOutlinedIcon      from "@mui/icons-material/AutoAwesome";
import QueryStatsOutlinedIcon       from "@mui/icons-material/QueryStats";
import TranslateOutlinedIcon        from "@mui/icons-material/TranslateOutlined";
import ScheduleSendOutlinedIcon     from "@mui/icons-material/ScheduleSend";

const FEATURES = [
  {
    icon: HeadsetMicOutlinedIcon,
    title: "Cloud PBX & HD Voice",
    body: "Crystal-clear G.722 HD calling in the browser via WebRTC. No hardware, no desk phones — your whole team on one cloud PBX.",
    color: "#1FB6D8", bg: "#DEF3F8",
  },
  {
    icon: AccountTreeOutlinedIcon,
    title: "Visual Call Flow Builder",
    body: "Design IVR menus, departments and bilingual voice prompts visually, then deploy your call center in one click.",
    color: "#5B5BE6", bg: "#E8E8FB",
  },
  {
    icon: AltRouteOutlinedIcon,
    title: "Smart Routing & Queues",
    body: "Queues, ring groups, round-robin and working-hours routing over SIP, GSM or voicemail — calls always reach the right agent.",
    color: "#3B7DF0", bg: "#E5EEFD",
  },
  {
    icon: WorkOutlinedIcon,
    title: "Built-in CRM & Tickets",
    body: "Caller profiles, tickets, tasks and follow-up callbacks — with screen-pop that surfaces context the moment a call rings.",
    color: "#F59E42", bg: "#FDEEDE",
  },
  {
    icon: AutoAwesomeOutlinedIcon,
    title: "AI Call Insights",
    body: "Transcripts, summaries, sentiment and intent for every answered call — saved with each customer in your CRM.",
    color: "#7A3FE4", bg: "#EEE8FC",
  },
  {
    icon: QueryStatsOutlinedIcon,
    title: "Live Analytics & Wallboard",
    body: "Real-time queues, agent status and KPIs on a glanceable wallboard, plus deep reports and QA scoring.",
    color: "#22B07D", bg: "#DEF3EC",
  },
];

export default function Features() {
  const { t } = useTranslation("home");
  return (
    <Box
      id="features"
      sx={{
        background: "#fff",
        py: { xs: "60px", md: "76px" },
        px: { xs: 3, md: "56px" },
      }}
    >
      <Box sx={{ maxWidth: 1320, mx: "auto" }}>
        {/* Section header */}
        <Box sx={{ textAlign: "center", maxWidth: 640, mx: "auto", mb: { xs: "44px", md: "44px" } }}>
          <Typography sx={{
            fontSize: "12px", fontWeight: 700, letterSpacing: ".08em",
            textTransform: "uppercase", color: "var(--brand-violet-ink)",
            fontFamily: "var(--font-ui)", mb: "10px", display: "block",
          }}>
            {t("featSectionLabel")}
          </Typography>
          <Typography
            component="h2"
            sx={{
              fontSize: { xs: "2rem", md: "2.375rem" },
              fontWeight: 800, letterSpacing: "-.02em",
              color: "#12142E", fontFamily: "var(--font-display)",
              lineHeight: 1.15,
            }}
          >
            {t("featSectionTitle")}
          </Typography>
          <Typography sx={{
            fontSize: "16px", color: "#6B7192",
            mt: "12px", lineHeight: 1.65, fontFamily: "var(--font-ui)",
          }}>
            {t("featSectionSub")}
          </Typography>
        </Box>

        {/* Cards grid */}
        <Box sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "repeat(2,1fr)", md: "repeat(3,1fr)" },
          gap: "20px",
        }}>
          {FEATURES.map(({ icon: Icon, title, body, color, bg }, i) => (
            <Box
              key={title}
              className="feat"
              sx={{
                background: "#fff",
                border: "1px solid #EEF0F8",
                borderRadius: "18px",
                boxShadow: "0 1px 2px rgba(46,55,120,.04),0 10px 26px rgba(46,55,120,.09)",
                padding: "26px",
                transition: "transform 0.24s cubic-bezier(.16,1,.3,1), box-shadow 0.24s ease",
                "&:hover": {
                  transform: "translateY(-5px)",
                  boxShadow: "0 20px 46px rgba(46,55,120,.15)",
                },
              }}
            >
              <Box sx={{
                width: 52, height: 52, borderRadius: "50%",
                background: bg,
                display: "grid", placeItems: "center",
                boxShadow: "0 6px 16px rgba(46,55,120,.10)",
                mb: "18px",
              }}>
                <Icon sx={{ fontSize: 26, color }} />
              </Box>
              <Typography sx={{
                fontSize: "18px", fontWeight: 700,
                color: "#12142E", mb: "8px", fontFamily: "var(--font-display)",
              }}>
                {t(`feat_${i}_title`, { defaultValue: title })}
              </Typography>
              <Typography sx={{
                fontSize: "14px", lineHeight: 1.6,
                color: "#6B7192", fontFamily: "var(--font-ui)",
              }}>
                {t(`feat_${i}_body`, { defaultValue: body })}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}
